import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { unifiedSearch, type UnifiedSearchResultItem } from '@/api/search';
import {
  parseSearchQuery,
  removeToken as removeSearchToken,
  stringifySearchTokens,
  type SearchToken,
} from '@/lib/search';
import { getErrorMessage } from '@/lib/error';
import { useAccountStore } from './accountStore';
import { useUIStore } from './uiStore';
import { mailEvents } from '@/events/mailEvents';

export type SearchResultItem = UnifiedSearchResultItem;

/** Page size for search results; a full page means more may exist. */
export const SEARCH_PAGE_SIZE = 50;

interface SearchOptions {
  /** If true, skip changing the view mode (used by command palette) */
  skipViewChange?: boolean;
  /** Type-ahead mode: keyword-only search, skips the vector/semantic pass */
  quick?: boolean;
}

/** Stable identity for dedupe when appending pages. */
const resultKey = (item: SearchResultItem): string => {
  if ('email' in item) return `email:${item.email.id}`;
  if ('thread' in item) return `thread:${item.thread.id}`;
  if ('task' in item) return `task:${item.task.id}`;
  if ('calendar_event' in item) return `event:${item.calendar_event.id}`;
  if ('contact' in item) return `contact:${item.contact.id}`;
  return `other:${JSON.stringify(item)}`;
};

interface SearchStoreState {
  query: string;
  tokens: SearchToken[];
  results: SearchResultItem[];
  loading: boolean;
  error: string | null;
  /** Server says more ranked results exist beyond what is loaded */
  hasMore: boolean;
  /** Tasks / calendar events that also matched (first page only) */
  related: SearchResultItem[];
  loadingMore: boolean;
  /**
   * Blocked-folder mail is excluded server-side by default. This is the
   * per-search override behind the "Blocked" switch on the results page.
   */
  includeBlocked: boolean;
  requestId: number;
  setQuery: (value: string) => void;
  setIncludeBlocked: (value: boolean) => Promise<void>;
  performSearch: (query: string, options?: SearchOptions) => Promise<void>;
  submitSearch: (query: string) => Promise<void>;
  loadMore: () => Promise<void>;
  removeToken: (tokenId: string) => Promise<void>;
  clearSearch: () => void;
}

const resetSelection = () => {
  mailEvents.emit('mail:selection:clear', undefined);
};

const resetSenderGroupingState = () => {
  mailEvents.emit('mail:senderGrouping:reset', undefined);
};

export const useSearchStore = create<SearchStoreState>()(
  conditionalDevtools(
    (set, get) => ({
    query: '',
    tokens: [],
    results: [],
    loading: false,
    error: null,
    hasMore: false,
    related: [],
    loadingMore: false,
    includeBlocked: false,
    requestId: 0,

    setQuery: (value) => set({ query: value }),

    setIncludeBlocked: async (value) => {
      if (get().includeBlocked === value) {
        return;
      }
      // Set first: performSearch reads the flag off the store when it builds
      // the request. skipViewChange keeps the reader/sender-group intact —
      // this re-runs the same search, it isn't new navigation.
      set({ includeBlocked: value });
      const query = get().query;
      if (query.trim()) {
        await get().performSearch(query, { skipViewChange: true });
      }
    },

    performSearch: async (query, options) => {
      const accountState = useAccountStore.getState();
      const accountId = accountState.selectedAccountId;
      if (!accountId) {
        return;
      }

      const trimmed = query.trim();
      if (!trimmed) {
        get().clearSearch();
        return;
      }

      const tokens = parseSearchQuery(trimmed);
      set({
        query: trimmed,
        tokens,
        loading: true,
        error: null,
        results: [],
        related: [],
        hasMore: false,
        loadingMore: false,
      });

      if (!options?.skipViewChange) {
        resetSelection();
        resetSenderGroupingState();
        useUIStore.getState().setView('search');
      }

      const requestId = get().requestId + 1;
      set({ requestId });

      try {
        // The server handles the unified 'all' sentinel natively: one request,
        // one comparable ranking space across accounts (tasks/events searched
        // once instead of per-account).
        const response = await unifiedSearch(accountId, trimmed, {
          limit: SEARCH_PAGE_SIZE,
          quick: options?.quick,
          includeBlocked: get().includeBlocked,
        });

        if (get().requestId === requestId) {
          set({
            results: response.results,
            related: response.related,
            loading: false,
            hasMore: response.hasMore,
          });
        }
      } catch (error: unknown) {
        if (get().requestId === requestId) {
          set({
            error: getErrorMessage(error) ?? 'Search failed',
            loading: false,
          });
        }
      }
    },

    submitSearch: async (query) => {
      set({ query });
      await get().performSearch(query);
    },

    loadMore: async () => {
      const state = get();
      if (state.loading || state.loadingMore || !state.hasMore || !state.query) {
        return;
      }
      const accountId = useAccountStore.getState().selectedAccountId;
      if (!accountId) {
        return;
      }

      // No requestId bump: this extends the current search. A new search or
      // clearSearch bumps requestId, which invalidates this append.
      const requestId = state.requestId;
      set({ loadingMore: true });

      try {
        const more = await unifiedSearch(accountId, state.query, {
          limit: SEARCH_PAGE_SIZE,
          offset: state.results.length,
          includeBlocked: state.includeBlocked,
        });

        if (get().requestId !== requestId) {
          return;
        }

        // The ranked list is deterministic per query, but dedupe on append as
        // insurance against index churn between pages.
        const seen = new Set(get().results.map(resultKey));
        const fresh = more.results.filter((item) => !seen.has(resultKey(item)));
        set({
          results: [...get().results, ...fresh],
          loadingMore: false,
          hasMore: more.hasMore,
        });
      } catch {
        // Keep hasMore so a further scroll can retry
        if (get().requestId === requestId) {
          set({ loadingMore: false });
        }
      }
    },

    removeToken: async (tokenId) => {
      const nextTokens = removeSearchToken(get().tokens, tokenId);
      const nextQuery = stringifySearchTokens(nextTokens);
      set({ tokens: nextTokens, query: nextQuery });
      await get().performSearch(nextQuery);
    },

    clearSearch: () => {
      set((state) => ({
        query: '',
        tokens: [],
        results: [],
        related: [],
        loading: false,
        error: null,
        hasMore: false,
        loadingMore: false,
        // Per-search override: a fresh search starts from the default again.
        includeBlocked: false,
        // Invalidate any in-flight search so its late response can't
        // repopulate the just-cleared results (F17).
        requestId: state.requestId + 1,
      }));
      // clearSearch clears SEARCH state only (X1/X2). It must NOT reset the mail
      // reader or exit a sender group — the command palette calls this on every
      // dismissal, and coupling selection/grouping resets here closed the reader
      // on Cmd+K→Escape while reading. Those resets belong to explicit
      // navigation: starting a full search (performSearch below, when it also
      // switches the view) and account switches already do them where needed.
    },
    }),
    { name: 'SearchStore' }
  )
);

// Switching accounts invalidates the current result set (it was scoped to the
// previous account). Close the search view and clear instead of showing stale
// results from the wrong account.
useAccountStore.subscribe((state, prevState) => {
  if (state.selectedAccountId === prevState.selectedAccountId) {
    return;
  }
  const search = useSearchStore.getState();
  if (!search.query && search.results.length === 0 && !search.loading) {
    return;
  }
  if (useUIStore.getState().view === 'search') {
    useUIStore.getState().setView('mailbox');
  }
  search.clearSearch();
});
